'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import {
  Zap, ChevronRight, CheckCircle2, ArrowRight, Menu, X,
  Calendar, Users, CreditCard, BarChart3, Shield, Smartphone,
  Star, Building2, Clock, TrendingUp, Award, Globe, Activity, ClipboardList
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'For Members', href: '#members' },
];

const SPORTS = ['🏸 Badminton', '💪 Gym', '🏏 Box Cricket', '🏐 Volleyball', '🎯 Archery', '🕺 Zumba', '⚽ Football', '🎾 Tennis'];

const FEATURES = [
  {
    icon: Calendar,
    title: 'Court & Slot Booking',
    desc: 'Real-time availability grid. Members book courts in seconds, arenas fill every slot.',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  {
    icon: Users,
    title: 'Member Management',
    desc: 'Full member profiles, sport enrollments, attendance history, and renewal alerts.',
    color: 'text-orange-500',
    bg: 'bg-orange-50',
  },
  {
    icon: CreditCard,
    title: 'Payments & Dues',
    desc: 'Track membership fees, collect online payments, and chase overdue dues automatically.',
    color: 'text-green-600',
    bg: 'bg-green-50',
  },
  {
    icon: BarChart3,
    title: 'Live Analytics',
    desc: 'Court utilization, revenue trends, and attendance patterns — all in one dashboard.',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
  },
  {
    icon: Smartphone,
    title: 'Member Self-Service',
    desc: 'Members log in, check their membership, book slots, and view attendance — no calls needed.',
    color: 'text-pink-600',
    bg: 'bg-pink-50',
  },
  {
    icon: Shield,
    title: 'Role-Based Access',
    desc: 'Owner, admin, reception, coach — each role sees exactly what they need.',
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
  },
  {
    icon: Activity,
    title: 'Activity Log',
    desc: 'Full audit trail of every action — member changes, payments, check-ins, and staff activity.',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
  },
  {
    icon: ClipboardList,
    title: 'Membership Management',
    desc: 'Manage enrollments, track dues, suspend or reactivate memberships, and export reports.',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
  },
];

const PRICING_PLANS = [
  {
    name: 'Starter',
    price: '₹1,499',
    period: '/month',
    desc: 'Perfect for single-sport arenas just getting started.',
    features: [
      'Up to 200 active members',
      '2 sports / facilities',
      'Court booking & attendance',
      'Basic payments tracking',
      'Member self-service portal',
      'Email support',
    ],
    cta: 'Start free trial',
    highlight: false,
  },
  {
    name: 'Growth',
    price: '₹3,499',
    period: '/month',
    desc: 'For multi-sport arenas scaling their operations.',
    features: [
      'Up to 1,000 active members',
      'Unlimited sports',
      'Online payments via Razorpay',
      'Advanced analytics & reports',
      'Add-ons & coaching modules',
      'Priority support',
    ],
    cta: 'Start free trial',
    highlight: true,
    badge: 'Most Popular',
  },
  {
    name: 'Pro',
    price: '₹7,999',
    period: '/month',
    desc: 'For large arenas and multi-location chains.',
    features: [
      'Unlimited members',
      'Multi-location management',
      'Custom branding per arena',
      'API access',
      'Dedicated account manager',
      'SLA-backed support',
    ],
    cta: 'Contact sales',
    highlight: false,
  },
];

const TESTIMONIALS = [
  {
    name: 'Karthik Rajan',
    role: 'Owner, Smash Zone Badminton',
    city: 'Chennai',
    quote: 'We went from WhatsApp chaos to zero missed bookings in a week. Our court utilization jumped from 60% to 91%.',
    rating: 5,
  },
  {
    name: 'Priya Menon',
    role: 'Manager, FitCore Gym',
    city: 'Bangalore',
    quote: 'Members love the self-service portal. Renewal reminders alone saved us 3 hours of calls every week.',
    rating: 5,
  },
  {
    name: 'Arjun Sharma',
    role: 'Director, Arena 360',
    city: 'Hyderabad',
    quote: 'Running badminton, gym, and cricket under one roof was a nightmare. ArenaSync made it manageable.',
    rating: 5,
  },
];

const HOW_IT_WORKS_ARENA = [
  { step: '01', title: 'Sign up your arena', desc: 'Create your account, add your sports, courts, and pricing in under 30 minutes.' },
  { step: '02', title: 'Add your members', desc: 'Import existing members or let them self-register via the member portal.' },
  { step: '03', title: 'Go live', desc: 'Start taking bookings, tracking attendance, and collecting payments from day one.' },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSport, setActiveSport] = useState(0);
  const sportIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    sportIntervalRef.current = setInterval(() => {
      setActiveSport((p) => (p + 1) % SPORTS.length);
    }, 1400);
    return () => {
      if (sportIntervalRef.current) clearInterval(sportIntervalRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">
      {/* ── Navbar ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100' : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#0f4c81] flex items-center justify-center">
              <Zap size={16} className="text-white" />
            </div>
            <span className={`font-bold text-lg tracking-tight ${scrolled ? 'text-slate-900' : 'text-white'}`}>
              ArenaSync
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className={`text-[13px] font-medium transition-colors hover:text-[#f97316] ${
                  scrolled ? 'text-slate-600' : 'text-white/80'
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/member-signin"
              className={`text-[13px] font-medium px-4 py-2 rounded-lg transition-colors ${
                scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white/90 hover:text-white'
              }`}
            >
              Member Login
            </Link>
            <Link
              href="/arena-signin"
              className={`text-[13px] font-medium px-4 py-2 rounded-lg transition-colors ${
                scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white/90 hover:text-white'
              }`}
            >
              Sign In
            </Link>
            <Link
              href="/arena-signup"
              className="text-[13px] font-semibold px-4 py-2 rounded-lg bg-[#f97316] text-white hover:bg-[#ea6c0a] transition-colors"
            >
              Arena Sign Up
            </Link>
          </div>

          <button
            className={`md:hidden p-2 rounded-lg ${scrolled ? 'text-slate-700' : 'text-white'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 px-5 py-4 space-y-3">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="block text-[14px] font-medium text-slate-700 py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                {l.label}
              </a>
            ))}
            <div className="pt-2 flex flex-col gap-2">
              <Link href="/member-signin" className="text-[13px] font-medium text-slate-700 py-2 text-center border border-slate-200 rounded-lg">
                Member Login
              </Link>
              <Link href="/arena-signin" className="text-[13px] font-medium text-slate-700 py-2 text-center border border-slate-200 rounded-lg">
                Sign In
              </Link>
              <Link href="/arena-signup" className="text-[13px] font-semibold py-2 text-center bg-[#f97316] text-white rounded-lg">
                Arena Sign Up
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0a3560]">
        {/* Animated gradient bg */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a3560] via-[#0f4c81] to-[#1a6bb5]" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#f97316]/10 blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#0f4c81]/40 blur-3xl translate-y-1/3 -translate-x-1/4" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-5 pt-24 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#f97316] animate-pulse" />
              <span className="text-white/80 text-[12px] font-medium">Sports Arena Management SaaS</span>
            </div>

            <h1 className="text-white text-4xl sm:text-5xl lg:text-[52px] font-bold leading-[1.1] mb-5">
              Run your arena.<br />
              <span className="text-[#f97316]">Not spreadsheets.</span>
            </h1>

            <p className="text-white/70 text-[16px] leading-relaxed mb-8 max-w-lg">
              ArenaSync is the all-in-one platform for sports arenas — bookings, memberships, attendance, and payments, built for badminton courts, gyms, cricket nets, and 10+ sports.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <Link
                href="/arena-signup"
                className="inline-flex items-center gap-2 bg-[#f97316] text-white font-semibold px-6 py-3 rounded-xl hover:bg-[#ea6c0a] transition-all active:scale-[0.98] text-[14px]"
              >
                Start free for your arena <ArrowRight size={16} />
              </Link>
              <Link
                href="/arena-signin"
                className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-medium px-6 py-3 rounded-xl hover:bg-white/20 transition-all text-[14px]"
              >
                Sign In <ChevronRight size={16} />
              </Link>
              <Link
                href="/member-signin"
                className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-medium px-6 py-3 rounded-xl hover:bg-white/20 transition-all text-[14px]"
              >
                I'm a member <ChevronRight size={16} />
              </Link>
            </div>

            <div className="flex items-center gap-6 flex-wrap">
              {[
                { val: '500+', label: 'Arenas' },
                { val: '1.2L+', label: 'Members' },
                { val: '10+', label: 'Sports' },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-white text-2xl font-bold tabular-nums">{s.val}</p>
                  <p className="text-white/50 text-[11px] font-medium uppercase tracking-wide">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-semibold text-[14px]">Today's Overview</span>
                  <span className="text-white/50 text-[12px]">Live</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { label: 'Check-ins', val: '87', delta: '+12%', color: 'text-green-400' },
                    { label: 'Bookings', val: '34', delta: '6 pending', color: 'text-orange-400' },
                    { label: 'Revenue', val: '₹18,400', delta: 'Today', color: 'text-blue-300' },
                    { label: 'Utilization', val: '94%', delta: 'Peak', color: 'text-purple-300' },
                  ].map((s) => (
                    <div key={s.label} className="bg-white/10 rounded-xl p-3">
                      <p className="text-white/50 text-[10px] uppercase tracking-wide mb-1">{s.label}</p>
                      <p className="text-white text-xl font-bold tabular-nums">{s.val}</p>
                      <p className={`text-[10px] mt-0.5 ${s.color}`}>{s.delta}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <p className="text-white/50 text-[10px] uppercase tracking-wide mb-2">Active Sport</p>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{SPORTS[activeSport].split(' ')[0]}</span>
                    <span className="text-white font-medium text-[14px]">{SPORTS[activeSport].split(' ').slice(1).join(' ')}</span>
                  </div>
                </div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-[#f97316] text-white rounded-xl px-4 py-2 shadow-lg">
                <p className="text-[11px] font-medium">Court B2 just booked</p>
                <p className="text-[10px] text-white/70">2 seconds ago</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sports ticker */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/20 backdrop-blur-sm border-t border-white/10 py-3 overflow-hidden">
          <div className="flex gap-8 animate-marquee whitespace-nowrap">
            {[...SPORTS, ...SPORTS].map((s, i) => (
              <span key={`ticker-${i}`} className="text-white/60 text-[13px] font-medium flex-shrink-0">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5">
          <div className="mb-12">
            <p className="text-[#f97316] text-[13px] font-semibold uppercase tracking-widest mb-2">Platform Features</p>
            <h2 className="text-slate-900 text-3xl sm:text-4xl font-bold mb-3">
              Everything your arena needs
            </h2>
            <p className="text-slate-500 text-[15px] max-w-xl">
              From the first booking to the last renewal — ArenaSync handles the operational layer so you can focus on the sport.
            </p>
          </div>

          {/* Bento grid — varied sizes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              const isWide = i === 0 || i === 4;
              return (
                <div
                  key={f.title}
                  className={`bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-md transition-shadow ${
                    isWide ? 'lg:col-span-2' : ''
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center mb-4`}>
                    <Icon size={20} className={f.color} />
                  </div>
                  <h3 className="text-slate-900 font-semibold text-[15px] mb-2">{f.title}</h3>
                  <p className="text-slate-500 text-[13px] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How it works (for arenas) ── */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#f97316] text-[13px] font-semibold uppercase tracking-widest mb-2">For Arena Owners</p>
              <h2 className="text-slate-900 text-3xl sm:text-4xl font-bold mb-4">
                Up and running in one afternoon
              </h2>
              <p className="text-slate-500 text-[15px] mb-8 leading-relaxed">
                No IT team needed. No lengthy onboarding. Just sign up, configure your sports, and start managing your arena the same day.
              </p>
              <div className="space-y-6">
                {HOW_IT_WORKS_ARENA.map((s) => (
                  <div key={s.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#0f4c81]/10 flex items-center justify-center">
                      <span className="text-[#0f4c81] font-bold text-[13px]">{s.step}</span>
                    </div>
                    <div>
                      <h4 className="text-slate-900 font-semibold text-[14px] mb-1">{s.title}</h4>
                      <p className="text-slate-500 text-[13px] leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                href="/sign-up-login"
                className="inline-flex items-center gap-2 mt-8 bg-[#0f4c81] text-white font-semibold px-6 py-3 rounded-xl hover:bg-[#0d3f6e] transition-all text-[14px]"
              >
                Set up your arena <ArrowRight size={16} />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Clock, label: 'Avg setup time', val: '< 2 hrs', color: 'bg-blue-50 text-blue-600' },
                { icon: TrendingUp, label: 'Avg utilization lift', val: '+28%', color: 'bg-green-50 text-green-600' },
                { icon: Users, label: 'Members managed', val: '1.2L+', color: 'bg-orange-50 text-orange-500' },
                { icon: Award, label: 'Arena satisfaction', val: '4.9 / 5', color: 'bg-purple-50 text-purple-600' },
                { icon: Globe, label: 'Sports supported', val: '10+', color: 'bg-pink-50 text-pink-600' },
                { icon: Building2, label: 'Active arenas', val: '500+', color: 'bg-indigo-50 text-indigo-600' },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                    <div className={`w-9 h-9 rounded-lg ${s.color.split(' ')[0]} flex items-center justify-center mb-3`}>
                      <Icon size={18} className={s.color.split(' ')[1]} />
                    </div>
                    <p className="text-slate-900 text-xl font-bold tabular-nums mb-0.5">{s.val}</p>
                    <p className="text-slate-500 text-[11px]">{s.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── For Members ── */}
      <section id="members" className="py-20 bg-[#0a3560]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b border-white/10">
                  <div className="w-10 h-10 rounded-full bg-[#f97316] flex items-center justify-center text-white font-bold text-[14px]">
                    RK
                  </div>
                  <div>
                    <p className="text-white font-semibold text-[14px]">Rahul Kumar</p>
                    <p className="text-white/50 text-[12px]">Smash Zone Badminton</p>
                  </div>
                  <span className="ml-auto bg-green-500/20 text-green-400 text-[11px] font-medium px-2.5 py-1 rounded-full">Active</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-white/50 text-[10px] uppercase tracking-wide mb-1">Membership</p>
                    <p className="text-white font-semibold text-[13px]">Monthly Pro</p>
                    <p className="text-white/50 text-[11px]">Expires Dec 31</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-white/50 text-[10px] uppercase tracking-wide mb-1">This Month</p>
                    <p className="text-white font-semibold text-[13px]">14 sessions</p>
                    <p className="text-white/50 text-[11px]">↑ 3 from last month</p>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <p className="text-white/50 text-[10px] uppercase tracking-wide mb-2">Upcoming Booking</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium text-[13px]">Court B2 — Badminton</p>
                      <p className="text-white/50 text-[11px]">Tomorrow, 7:00 AM – 8:00 AM</p>
                    </div>
                    <span className="bg-blue-500/20 text-blue-300 text-[11px] px-2 py-0.5 rounded-full">Confirmed</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <p className="text-[#f97316] text-[13px] font-semibold uppercase tracking-widest mb-2">For Members</p>
              <h2 className="text-white text-3xl sm:text-4xl font-bold mb-4">
                Your arena, in your pocket
              </h2>
              <p className="text-white/70 text-[15px] mb-6 leading-relaxed">
                Log in once and see your arena's branding, book courts, check your membership status, and track every session — all from one clean portal.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Book available court slots instantly',
                  'View membership status and expiry',
                  'Track attendance and session history',
                  'Login via email/password or phone OTP',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white/80 text-[14px]">
                    <CheckCircle2 size={16} className="text-[#f97316] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                href="/member-portal/login"
                className="inline-flex items-center gap-2 bg-[#f97316] text-white font-semibold px-6 py-3 rounded-xl hover:bg-[#ea6c0a] transition-all text-[14px]"
              >
                Go to member portal <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <p className="text-[#f97316] text-[13px] font-semibold uppercase tracking-widest mb-2">Pricing</p>
            <h2 className="text-slate-900 text-3xl sm:text-4xl font-bold mb-3">
              Simple, transparent pricing
            </h2>
            <p className="text-slate-500 text-[15px]">
              No setup fees. No per-member charges. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-6 border relative ${
                  plan.highlight
                    ? 'bg-[#0f4c81] border-[#0f4c81] shadow-xl shadow-[#0f4c81]/20'
                    : 'bg-white border-slate-200'
                }`}
              >
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#f97316] text-white text-[11px] font-semibold px-3 py-1 rounded-full">
                    {plan.badge}
                  </span>
                )}
                <div className="mb-5">
                  <h3 className={`font-bold text-[16px] mb-1 ${plan.highlight ? 'text-white' : 'text-slate-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-[12px] mb-4 ${plan.highlight ? 'text-white/60' : 'text-slate-500'}`}>
                    {plan.desc}
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-3xl font-bold tabular-nums ${plan.highlight ? 'text-white' : 'text-slate-900'}`}>
                      {plan.price}
                    </span>
                    <span className={`text-[13px] ${plan.highlight ? 'text-white/60' : 'text-slate-500'}`}>
                      {plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-[13px] ${plan.highlight ? 'text-white/80' : 'text-slate-600'}`}>
                      <CheckCircle2 size={14} className={`flex-shrink-0 mt-0.5 ${plan.highlight ? 'text-[#f97316]' : 'text-green-500'}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  href="/sign-up-login"
                  className={`block text-center py-2.5 rounded-xl text-[13px] font-semibold transition-all ${
                    plan.highlight
                      ? 'bg-[#f97316] text-white hover:bg-[#ea6c0a]'
                      : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <p className="text-[#f97316] text-[13px] font-semibold uppercase tracking-widest mb-2">Testimonials</p>
            <h2 className="text-slate-900 text-3xl font-bold">Arena owners love it</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={14} className="text-[#f97316] fill-[#f97316]" />
                  ))}
                </div>
                <p className="text-slate-700 text-[14px] leading-relaxed mb-5">"{t.quote}"</p>
                <div>
                  <p className="text-slate-900 font-semibold text-[13px]">{t.name}</p>
                  <p className="text-slate-500 text-[12px]">{t.role}</p>
                  <p className="text-slate-400 text-[11px]">{t.city}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-[#0a3560]">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="text-white text-3xl sm:text-4xl font-bold mb-4">
            Ready to sync your arena?
          </h2>
          <p className="text-white/70 text-[15px] mb-8">
            Join 500+ arenas already running on ArenaSync. Free 14-day trial, no credit card needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/sign-up-login"
              className="inline-flex items-center justify-center gap-2 bg-[#f97316] text-white font-semibold px-8 py-3.5 rounded-xl hover:bg-[#ea6c0a] transition-all text-[15px]"
            >
              Start free trial <ArrowRight size={16} />
            </Link>
            <Link
              href="/member-portal/login"
              className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white font-medium px-8 py-3.5 rounded-xl hover:bg-white/20 transition-all text-[15px]"
            >
              Member login
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-slate-900 py-10">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#0f4c81] flex items-center justify-center">
              <Zap size={14} className="text-white" />
            </div>
            <span className="text-white font-bold text-[15px]">ArenaSync</span>
          </div>
          <p className="text-slate-500 text-[12px]">© 2026 ArenaSync. All rights reserved.</p>
          <div className="flex gap-5">
            {['Privacy', 'Terms', 'Contact'].map((l) => (
              <a key={l} href="#" className="text-slate-500 text-[12px] hover:text-white transition-colors">{l}</a>
            ))}
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}</style>
    </div>
  );
}
